export type VideoFulfillmentStatus = 'full' | 'partial' | 'queuedOffline';

export const OFFLINE_VOD_STATUS_MESSAGE = 'Device is offline. Available footage for the selected time cannot be verified on the device. The request will be queued and uploaded when the device reconnects.';

export const OFFLINE_VOD_TIMELINE_HINT_LINES = [
  'Available footage for the selected time cannot be verified on the device.',
  'The request will be queued and uploaded when the device reconnects.',
];

export const OFFLINE_VOD_SUCCESS_MESSAGE = 'VoD request queued. The device is offline — the selected time could not be verified on the device. Upload will start when the device reconnects.';

export const VOD_NOT_AVAILABLE_LINES = [
  'No footage was available on the device for the requested time.',
  'Your VoD request could not be fulfilled.',
];

const VOD_FAILURE_STATUSES = new Set([
  'failedFinally',
  'failedAttemptsLimitExceeded',
  'failedDeviceRemovedFromFleet',
]);

export type VideoFulfillmentView = {
  fulfillmentMessage?: string | null;
  fulfillmentStatus?: VideoFulfillmentStatus | null;
  status?: string;
  requestedFrom?: string | number | null;
  requestedTo?: string | number | null;
  from?: string | number | null;
  to?: string | number | null;
};

function isQueuedOfflineAwaitingDevice(video: VideoFulfillmentView): boolean {
  return video.fulfillmentStatus === 'queuedOffline' && (!video.status || video.status === 'pending');
}

function shouldHideQueuedOfflineFulfillmentDetails(video: VideoFulfillmentView): boolean {
  return video.fulfillmentStatus === 'queuedOffline'
    && (video.status === 'startedProcessing' || video.status === 'completed');
}

export type TimeRangeMs = [number, number];
// VOD fullfillment
export function intersectTimeRange(
  requestedFrom: number,
  requestedTo: number,
  availableRanges: TimeRangeMs[],
): { from: number; to: number; isPartial: boolean } | null {
  const { clipFrom, clipTo } = availableRanges.reduce(
    (acc, [start, end]) => {
      const overlapStart = Math.max(requestedFrom, start);
      const overlapEnd = Math.min(requestedTo, end);
      if (overlapStart < overlapEnd) {
        return {
          clipFrom: Math.min(acc.clipFrom, overlapStart),
          clipTo: Math.max(acc.clipTo, overlapEnd),
        };
      }
      return acc;
    },
    { clipFrom: Infinity, clipTo: -Infinity },
  );

  if (!Number.isFinite(clipFrom) || !Number.isFinite(clipTo)) {
    return null;
  }

  return {
    from: clipFrom,
    to: clipTo,
    isPartial: clipFrom > requestedFrom || clipTo < requestedTo,
  };
}

export function getFulfillmentStatusLabel(
  fulfillmentStatus?: VideoFulfillmentStatus | null,
  fallbackStatus?: string,
  videoStatus?: string,
): string {
  switch (fulfillmentStatus) {
    case 'queuedOffline':
      if (videoStatus && !isQueuedOfflineAwaitingDevice({ fulfillmentStatus, status: videoStatus })) {
        return fallbackStatus ?? '';
      }
      return 'Queued — device offline (time unverified)';
    case 'partial':
      return fallbackStatus ?? 'Partial footage uploaded';
    case 'full':
      return fallbackStatus ?? 'Full footage requested';
    default:
      return fallbackStatus ?? '';
  }
}

export function getFulfillmentDetailMessage(video: VideoFulfillmentView): string | null {
  if (shouldHideQueuedOfflineFulfillmentDetails(video)) {
    return null;
  }
  if (video.fulfillmentMessage) {
    return video.fulfillmentMessage;
  }
  if (video.fulfillmentStatus === 'queuedOffline' && video.status && VOD_FAILURE_STATUSES.has(video.status)) {
    return VOD_NOT_AVAILABLE_LINES.join(' ');
  }
  if (isQueuedOfflineAwaitingDevice(video)) {
    return OFFLINE_VOD_STATUS_MESSAGE;
  }
  if (video.fulfillmentStatus === 'partial' && video.requestedFrom && video.requestedTo && video.from && video.to) {
    const fmt = (value: string | number): string => new Date(value).toLocaleString();
    return `Requested ${fmt(video.requestedFrom)} – ${fmt(video.requestedTo)}, received ${fmt(video.from)} – ${fmt(video.to)}.`;
  }
  return null;
}

export function getFulfillmentDetailLines(video: VideoFulfillmentView): string[] {
  if (shouldHideQueuedOfflineFulfillmentDetails(video)) {
    return [];
  }
  if (video.fulfillmentStatus === 'queuedOffline' && video.status && VOD_FAILURE_STATUSES.has(video.status)) {
    return [...VOD_NOT_AVAILABLE_LINES];
  }
  if (isQueuedOfflineAwaitingDevice(video)) {
    return [...OFFLINE_VOD_TIMELINE_HINT_LINES];
  }

  const detail = getFulfillmentDetailMessage(video);
  if (!detail) {
    return [];
  }

  if (video.fulfillmentMessage || video.fulfillmentStatus === 'partial') {
    return detail
      .split(/(?<=[.!?])\s+/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  return [detail];
}
